import get from 'get-value';
import { createRange, getCellAddress, offsetCell, parseRangeAddress, parseCellAddress, normalizeWorksheetName } from 'shared/excel/excel';
import { generateUuid, toCategory, generateFlowstepName, } from 'shared/utils/flowstep';
import { rangeIndexesToString } from 'shared/excel/excel';
import { RangeAddress } from 'shared/excel/rangeAddress';
import { DataTable } from 'src/services/runtime/dataTable';
import { areStringsInsensitivelyEqual, asArray } from 'shared/utils/utils';
import { NamedItem } from './runtime/namedItem';
import { PasswordProtectionError } from 'shared/utils/flowstep';
import { Limits } from 'shared/excel/runtime/flowstepExecution';


const ParamKey = {
  Flow: 'flows',
  Flowsteps: 'flowsteps',
  OnlyOnActiveWorksheet: 'onlyOnActiveWorksheet',
  Lookups: 'asssigned_lookups',
};

export const SurroundingMode = {
  disabled: 'disabled',
  default: 'default',
  tableStart: 'tableStart',
};

const PlatformLimits = {
  [Office.PlatformType.OfficeOnline]: Limits.OfficeWeb,
  [Office.PlatformType.iOS]: Limits.iOs,
  [Office.PlatformType.Mac]: Limits.Mac,
};

// --- OFFICE MODEL ---
//
class OfficeModel {
  constructor() {
    this.platformType = 'unknown';
  }

  get isPlatformiOS() {
    return this.platformType === Office.PlatformType.iOS;
  }

  get platformLimits() {
    return PlatformLimits[this.platformType] ?? Limits.OfficeWeb;
  }

  async startupTaskpane() {
    // await Office.addin.setStartupBehavior(Office.StartupBehavior.load);

    // //TOOD: remove it after all clients migrate to showAsTaskpane
    // if (this.getParam('Office.AutoShowTaskpaneWithDocument')) {
    //   await this.removeParam('Office.AutoShowTaskpaneWithDocument');
    // }

    // if (this.getParam('xappex.AutoShowTaskpaneWithDocument')) {
    //   await Office.addin.showAsTaskpane();
    // }
  }

  async setVisibilityMode(visible) {
    console.debug('setVisibilityMode', visible);
    await Office.addin.setStartupBehavior(Office.StartupBehavior.none);
    // await this.setParam('xappex.AutoShowTaskpaneWithDocument', visible);
  }

  isSetSupported(version) {
    return Office.context.requirements.isSetSupported('ExcelApi', version);
  }

  getActiveWorksheet() {
    return Excel.run(async (context) => {
      const worksheet = context.workbook.worksheets.getActiveWorksheet().load(['id', 'name']);
      await context.sync();
      const { id, name } = worksheet;
      return { id, name };
    });
  }

  activateWorksheet(worksheetId, rangeToSelect = '') {
    return Excel.run(async (context) => {
      try {
        const worksheet = context.workbook.worksheets.getItem(worksheetId);
        worksheet.activate();
        if (rangeToSelect) {
          worksheet.getRange(rangeToSelect).select();
        }
        await context.sync();
      }
      catch (e) {
        // Nothing to do
      }
    });
  }

  async getSelectedRanges() {
    return await Excel.run(async (ctx) => {
      const ranges = ctx.workbook.getSelectedRanges().load(['address', 'areaCount', 'worksheet']);
      const areas = ranges.areas.load(['items']);
      await ctx.sync();

      const addresses = ranges.address.split(',').map(x => new RangeAddress(x));
      return { addresses, items: areas.items, count: ranges.areaCount, worksheetId: ranges.worksheet.id };
    });
  }

  async subscribeWorksheetsEvents(callback) {
    await Office.onReady();
    if (!this.isSetSupported(1.7)) { return }

    await Excel.run(async (context) => {
      callback(context.workbook.worksheets);
      await context.sync();
    });
  }

  async getWorksheetName(pattern) {
    const getSheets = () => this.getAllWorksheets();
    return await this.getWorksheetNameImpl(pattern, getSheets, true);
  }

  async getWorksheetNameWithCtx(context, pattern, notrmalizePattern) {
    const getSheets = () => this.getAllWorksheetsWithCtx(context, false);
    return await this.getWorksheetNameImpl(pattern, getSheets, notrmalizePattern);
  }

  async getWorksheetNameImpl(pattern, getSheetsFn, normalizePattern) {
    let name;
    if (pattern && (!normalizePattern || (pattern = normalizeWorksheetName(pattern)))) {
      const sheets = await getSheetsFn();
      const names = new Set(sheets.map(({ name }) => name.toLowerCase()));

      let index = -1;
      do {
        const postfix = ++index ? index.toString() : '';
        name = (pattern.length + postfix.length) > 31
          ? `${pattern.substring(0, 31 - (postfix.length + 1))}…${postfix}`
          : `${pattern}${postfix}`;
      } while (names.has(name.toLocaleLowerCase()));
    }

    return name;
  }

  async createWorksheet(pattern) {
    return await Excel.run((context) => this.createWorksheetWithCtx(context, pattern, true));
  }

  async createWorksheetWithCtx(context, pattern, normalizePattern = false) {
    const name = await this.getWorksheetNameWithCtx(context, pattern, normalizePattern);
    const worksheet = context.workbook.worksheets.add(name).load(['id', 'name']);
    await context.sync()
    worksheet.activate();
    return worksheet;
  }

  async getActiveCell() {
    return await Excel.run(async (context) => {
      const activeCell = context.workbook.getActiveCell().load(['address', 'columnIndex', 'rowIndex', 'values']);
      await context.sync();
      return activeCell;
    });
  }

  getSelectedRangeReference() {
    return Excel.run(async (ctx) => {
      const range = ctx.workbook.getSelectedRange().load(['columnIndex', 'rowIndex']);
      await ctx.sync();
      return getCellAddress(range.columnIndex, range.rowIndex);
    });
  }

  async getValuesByIndexes(sheet, startRowIndex, startColIndex, rowCount, columnCount) {
    const range = sheet.getCell(startRowIndex, startColIndex);

    if (range.getResizedRange) {
      range.getResizedRange(rowCount, columnCount);
      range.load(['values', 'address']);
      await sheet.context.sync();

      return range.values;
    }

    range.load(['address']);
    await sheet.context.sync();
    const address = createRange(range.address.split('!')[1].split(':')[0], columnCount, rowCount);
    const valuesRange = sheet.getRange(address);
    valuesRange.load(['values', 'address']);
    await sheet.context.sync();

    return valuesRange.values;
  }

  getDocumentOnlinePath() {
    return new Promise((resolve, reject) => {
      Office.context.document.getFilePropertiesAsync((result) => {
        if (result.status !== 'succeeded') { return reject(result.error || new Error('Failed to get file properties')) }
        const url = get(result, 'value.url') || '';

        console.debug('getDocumentOnlinePath', url || result);
        url.match(/^https?:\//) ? resolve(url) : reject(new Error(url));
      });
    });
  }

  getAllWorksheets(includeVeryHidden = false) {
    return Excel.run(async (ctx) => {
      return await this.getAllWorksheetsWithCtx(ctx, includeVeryHidden);
    });
  }

  async getAllWorksheetsWithCtx(ctx, includeVeryHidden = false) {
    const worksheets = ctx.workbook.worksheets.load(['id', 'name', 'visibility']);
    await ctx.sync();
    return worksheets.items.reduce((out, { id, name, visibility }) => {
      if (includeVeryHidden || visibility !== Excel.SheetVisibility.veryHidden) {
        out.push({ id, name });
      }
      return out;
    }, []);
  }

  async setValues(worksheet, range, values) {
    worksheet.getRange(range).values = values;
    await worksheet.context.sync();
  }

  async insertRange(worksheet, table, target, values, hasHeaders) {
    const { rowIndex: vr0, columnIndex: vc0 } = parseCellAddress(target);
    const vc1 = vc0 + values[0].length - 1;

    if (table) {
      const range = table.getRange().load(['address']);
      const bodyRange = table.getDataBodyRange().load(['address']);
      await worksheet.context.sync();

      const { first, last } = parseRangeAddress(range.address);
      const { rowIndex: tr0, columnIndex: tc0 } = parseCellAddress(first);
      const { rowIndex: tr1, columnIndex: tc1 } = parseCellAddress(last);

      if (hasHeaders && vc1 > tc1) { // Add necessary amount of columns into table if needed
        const data = [...Array((tr1 - tr0) + 1).keys()].map(i => [null]);
        for (let i = 0; i < (vc1 - tc1); ++i) {
          table.columns.add((tc1 - tc0) + i + 1, data);
        }
      }

      const { rowIndex: br1 } = parseCellAddress(parseRangeAddress(bodyRange.address).last);
      if (vr0 + (values.length - 1) > br1) { // Add necessary amount of rows into table if needed
        const cols = Math.max(values[0].length, (tc1 - tc0) + 1);

        const data = [...Array(cols).keys()].map(i => null);
        if (this.isSetSupported(1.4)) {
          table.rows.add(null, [...Array(vr0 + (values.length - 1) - br1).keys()].map(i => data));
        }
        else {
          for (let i = br1; i < vr0 + (values.length - 1); ++i) {
            table.rows.add(null, [data]);
          }
        }
      }
    }

    // const part = this.platformType !== Office.PlatformType.OfficeOnline
    //   ? values.length : Math.round(2000 / values[0].length);
    const part = values.length;

    for (let i = 0; i < values.length; i = i + part) {
      const vals = values.slice(i, i + part);
      const address = createRange(target, values[0].length, vals.length);

      const range = worksheet.getRange(address);
      range.values = vals;

      await worksheet.context.sync();
      target = offsetCell(target, 0, part);
    }
  }

  async clearUnusedRange(worksheet, table, target, cols, rows) {
    const usedRange = (table ? table.getDataBodyRange() : worksheet.getUsedRange()).load(['address']);
    await worksheet.context.sync();

    const { last } = parseRangeAddress(usedRange.address);
    if (!last) { return }

    const { rowIndex: br1, columnIndex: bc1 } = parseCellAddress(last);
    const { rowIndex: vr0, columnIndex: vc0 } = parseCellAddress(target);

    if (table) { cols = (bc1 - vc0) + 1 } // It's only allowed to delete whole row in a table

    const totalRows = br1 - (vr0 + rows);

    try {
      if (totalRows >= 0) {
        const range = createRange(getCellAddress(vc0, vr0 + rows), cols, totalRows + 1);
        worksheet.getRange(range).delete(Excel.DeleteShiftDirection.up);
        await worksheet.context.sync();
      }
    }
    catch (error) {
      if (totalRows !== 0) { throw error }
    }
  }

  // Flowsteps
  _convertFlowsteps = async (flowsteps) => {
    const worksheets = await this.getAllWorksheets();
    const wsMap = new Map(worksheets.map(item => [item.id, item.name]));

    return Object.keys(flowsteps).reduce((out, key) => {
      const flowstep = flowsteps[key];

      //WORKARROUND: try to catch case when we loose flowsteps
      let worksheetName = wsMap.get(flowstep.worksheetId);
      if (!worksheetName) { worksheetName = 'Deleted' }

      // if (worksheetName = wsMap.get(flowstep.worksheetId)) {
      flowstep.id = flowstep.id || generateUuid();
      flowstep.category = flowstep.category || toCategory({ type: flowstep.type });
      flowstep.name = flowstep.name || generateFlowstepName({ flowstep, worksheetName });
      out[`${flowstep.id}`] = flowstep;
      // }
      return out;
    }, {});
  }

  async getFlowstep(worksheetId, target) {
    const data = await this._getItems(ParamKey.Flowsteps, this._convertFlowsteps);
    return Object.values(data).filter(item => {
      const wsMatch = worksheetId && item.worksheetId === worksheetId;
      const targetMatch = target && item.target === target;
      return target ? (wsMatch && targetMatch) : (!worksheetId || wsMatch)
    });
  }

  async getOnlyOnActiveWorksheet() {
    return await this._getItems(ParamKey.OnlyOnActiveWorksheet);
  }

  async setOnlyOnActiveWorksheet(value) {
    return await this.setParam(ParamKey.OnlyOnActiveWorksheet, value)
  }

  async setFlowstep(flowstep) {
    return await this._setItem(ParamKey.Flowsteps, flowstep, this._convertFlowsteps);
  }

  async deleteFlowstep(flowstep) {
    return await this._deleteItem(ParamKey.Flowsteps, flowstep, this._convertFlowsteps);
  }

  async replaceFlowsteps(flowsteps) {
    return await this._replaceItems(ParamKey.Flowsteps, flowsteps);
  }

  // Flows
  async getFlows() {
    return Object.values(await this._getItems(ParamKey.Flow));
  }

  async setFlow(flow) {
    return await this._setItem(ParamKey.Flow, flow);
  }

  async deleteFlow(flow) {
    return await this._deleteItem(ParamKey.Flow, flow);
  }

  async replaceFlows(flows) {
    return await this._replaceItems(ParamKey.Flow, flows);
  }


  // Asigned Lookups
  _convertAssignedLookups(lookups) {
    return Object.keys(lookups).reduce((out, key) => {
      const lookup = lookups[key];
      //backward compatibility
      if (lookup.address) {
        lookup.address = new RangeAddress(lookup.address);
      }
      out[key] = lookup;
      return out;
    }, {});
  }

  async getAssignedLookups() {
    return Object.values(await this._getItems(ParamKey.Lookups, this._convertAssignedLookups));
  }

  async replaceAssignedLookups(lookups) {
    const converted = lookups.map(lookup => ({
      ...lookup,
      //backward compability
      address: lookup.address?.shortAddress
    }));
    await this._replaceItems(ParamKey.Lookups, converted);
  }

  // Items
  async _getItems(key, convertor) {
    const items = this.getParam(key) ?? {};
    // console.log('!!!!! office::_getItems: ', key, items);
    const data = convertor ? await convertor(items) : items;
    // console.log('office::_getItems: ', key, data);
    return data;
  }

  async _setItem(key, item, convertor) {
    const items = asArray(item);
    const data = await this._getItems(key, convertor);
    // console.log('office::_setItem: ', key, data, items);
    items.forEach(item => data[item.id] = item);
    await this.setParam(key, data);
    return data;
  }

  async _deleteItem(key, item, convertor) {
    const items = asArray(item);
    const data = await this._getItems(key, convertor);
    // console.log('office::_deleteItem: ', key, data, items);
    items.forEach(item => delete data[item.id]);
    await this.setParam(key, data);
    return data;
  }

  async _replaceItems(key, items) {
    const data = {};
    items.forEach(item => data[item.id] = item);
    return await this.setParam(key, data);
  }

  getParam(key) {
    return Office.context.document.settings.get(key);
  }

  async setParam(key, value) {
    // console.log(`Set Excel Param '${key}' to ${value}`);

    return await new Promise((resolve, reject) => {
      Office.context.document.settings.set(key, value);
      Office.context.document.settings.saveAsync((res) => {
        if (res.status === Office.AsyncResultStatus.Failed) { return reject(res.error) }
        resolve(value);
      });
    });
  }

  async removeParam(key) {
    return await new Promise((resolve, reject) => {
      Office.context.document.settings.remove(key);
      Office.context.document.settings.saveAsync((res) => {
        if (res.status === Office.AsyncResultStatus.Failed) { return reject(res.error) }
        resolve();
      });
    });
  }

  async _loadTables(targetRange) {
    try {
      let tables = targetRange.getTables(/* fullyContained */ false).load(['items']);
      await targetRange.context.sync();
      return tables.items;
    }
    catch (e) {
      console.log(`Unable get tables via 'range.getTables()': `, e.message);
      return [];
    }
  }

  async getDataTable(targetRange, { alternativeRange = null, surroundingMode = SurroundingMode.default } = {}) {

    const tables = await this._loadTables(targetRange);

    if (tables.length) {
      const table = tables[0];
      return await DataTable.fromTable(table);
    }

    if (surroundingMode === SurroundingMode.disabled) {
      return alternativeRange ? await DataTable.fromRange(alternativeRange) : null;;
    }

    try {
      const surroundingRange = surroundingMode === SurroundingMode.default
        ? targetRange.getSurroundingRegion()
        : await this.getSurroundingRangeForDataTable(targetRange);

      return await DataTable.fromRange(surroundingRange);
    }
    catch (error) {
      console.log('No surrounding range found');
      return alternativeRange ? await DataTable.fromRange(alternativeRange) : null;
    }
  }


  /**
   * @param targetRange Top-left cell of the surrounding range
   */
  async getSurroundingRangeForDataTable(targetRange) {
    const surrounding = targetRange.getSurroundingRegion();
    surrounding.load(['rowIndex', 'rowCount', 'columnIndex', 'columnCount']);
    targetRange.load(['rowIndex', 'columnIndex']);
    await targetRange.worksheet.context.sync();

    const columns = surrounding.columnCount - (targetRange.columnIndex - surrounding.columnIndex) - 1;
    const rows = surrounding.rowCount - (targetRange.rowIndex - surrounding.rowIndex) - 1;
    const res = targetRange.getResizedRange(rows, columns);

    return res
  }


  async getSelectedTableColumns(targetRange) {
    const tables = await this._loadTables(targetRange);
    if (tables.length) {
      const table = tables[0];

      table.columns.load("items");
      table.load('name');
      await table.context.sync();
      const columns = table.columns.items;
      return { table, columns };

    }
    return {};
  }

  async _getTable(tableId, context) {
    const table = context.workbook.tables.getItemOrNullObject(tableId);
    await context.sync();
    if (!table.isNullObject) {
      table.columns.load("items");
      await table.context.sync();
      const columns = table.columns.items;
      return { table, columns };
    }
    return {};
  }

  async getTable(tableId, context) {
    if (context) {
      return await this._getTable(tableId, context);
    }
    else {
      return await Excel.run(async (ctx) => await this._getTable(tableId, ctx));
    }
  }

  async getFlowstepTarget({ worksheetId, targetCell } = {}) {
    return await Excel.run(async (context) => {
      let table = null;
      let worksheet = null;
      let range = null;

      try {
        if (targetCell && worksheetId) {
          worksheet = context.workbook.worksheets.getItemOrNullObject(worksheetId);
          await context.sync();
          if (worksheet.isNullObject) {
            return {
              targetCell: null,
              table: null,
              worksheetId: null,
            }
          }

          range = worksheet.getRange(targetCell);
        }
        else {
          const ranges = context.workbook.getSelectedRanges().areas.load(['items']);
          await context.sync();
          range = ranges.getItemAt(0);
          worksheet = range.worksheet;
        }

        worksheet.load(['id', 'name']);
        range.load(['worksheet', 'rowIndex', 'columnIndex']);

        table = await this.getDataTable(range);
      }
      catch (e) { // when worksheetId is not valid any more (removed)
        console.log(`Worksheet id is not valid any more: `, e.message);
        range = null;
        worksheet = null;
      }

      const basicInfo = {
        targetCell: range ? rangeIndexesToString({ rowIndex: range.rowIndex, columnIndex: range.columnIndex }) : targetCell,
        worksheetId: worksheet ? worksheet.id : worksheetId,
        worksheetName: worksheet ? worksheet.name : 'Removed worksheet',
      };

      if (!table) {
        return basicInfo;
      }

      const headers = await table.getHeaders();
      const { rowIndex, columnIndex, rowCount, columnCount } = table.range;

      return Object.assign(basicInfo, {
        targetCell: rangeIndexesToString({ rowIndex, columnIndex }),
        table: {
          header: { address: table.headerRowRange.address, values: headers },
          dataBodyAddress: table.dataBodyRange.address,
          rangeAddress: rangeIndexesToString({ rowIndex, columnIndex, rowCount, columnCount })
        }
      });
    });
  }

  async toggleExcelEvents(context) {
    context.runtime.load('enableEvents');
    await context.sync();
    context.runtime.enableEvents = !context.runtime.enableEvents;
    await context.sync();
  }

  async createDataTable(worksheet, startCell, cols, rows) {
    const range = createRange(startCell, cols, rows);
    const table = worksheet.tables.add(range, true);

    if (this.isSetSupported(1.2)) {
      const tableRange = table.getRange();
      tableRange.format.autofitColumns();
      tableRange.format.autofitRows();
    }
    await worksheet.context.sync();
    return table;
  }

  async getWorksheetTableForTarget(worksheet, target) {
    const tables = worksheet.tables.load('name');
    await worksheet.context.sync();

    for (const table of tables.items) {
      const range = table.getRange().load(['address']);
      await worksheet.context.sync();

      const { first } = parseRangeAddress(range.address);
      if (first === target) {
        return table;
      }
    }
    return false;
  }

  async getCalculationMode() {
    return await Excel.run(async (context) => {
      const app = context.workbook.application.load(['calculationMode']);
      await context.sync();
      return app.calculationMode;
    });
  }

  async setCalculationMode(calculationMode) {
    return await Excel.run(async (context) => {
      context.workbook.application.calculationMode = calculationMode;
      await context.sync();
    });
  }

  async getNamedItems() {
    return await Excel.run(async (context) => {
      const items = context.workbook.names;
      items.load('items');
      await context.sync();
      return items.items.map(item => new NamedItem(item));
    })
  }

  async calculateFormulas() {
    return await Excel.run(async (context) => {
      context.workbook.application.calculate(Excel.CalculationType.full);
      await context.sync();
    })
  }

  async runWithoutWorksheetProtection(task, { worksheet, password }) {
    const protection = worksheet.protection;
    protection.load(['options', 'protected']);
    await protection.context.sync();
    const isProtected = protection.protected;
    let options;

    const unprotect = async () => {
      try {
        options = protection.options;
        protection.unprotect(password);
        await protection.context.sync();
      }
      catch (error) {
        if (areStringsInsensitivelyEqual(error.code, 'InvalidArgument')) { throw new PasswordProtectionError(); }
        throw error;
      }
    }

    const protect = async () => {
      protection.protect(options, password);
      await protection.context.sync();
    }

    if (isProtected) { await unprotect() };

    try {
      return await task();
    }
    finally {
      if (isProtected) { await protect(); };
    }
  }

  async refreshPivotTables() {
    await Excel.run(async (ctx) => {
      ctx.workbook.pivotTables.refreshAll();
      await ctx.sync()
    });
  }

  async createRunButton(rangeAddress, text) {

    const format_border = (border_object, border_names, style, color, weight) => {
      for (const border of border_names) {
        border_object.getItem(border).style = style
        border_object.getItem(border).color = color
        border_object.getItem(border).weight = weight
      }
    }

    await Excel.run(async (context) => {
      const { customRange } = rangeAddress;
      const range = context.workbook.names.getItem(customRange).getRange();
      range.load(['rowCount', 'columnCount']);
      await context.sync();

      range.merge();
      range.format.horizontalAlignment = "Center";
      range.format.verticalAlignment = "Center";
      range.format.columnWidth = 75;
      range.format.fill.color = "lightgrey";

      format_border(range.format.borders,
        ["EdgeBottom", "EdgeRight"], "Continuous", "darkGrey", "thick");
      format_border(range.format.borders,
        ["EdgeTop", "EdgeLeft"], "Continuous", "whiteSmoke", "thick");

      const values = new Array(range.rowCount).fill(new Array(range.columnCount));
      values[0][0] = text;
      range.values = values;

      await context.sync();
    });
  }

  async deleteRunButton(rangeAddress) {
    await Excel.run(async (context) => {
      const { address, worksheetId, customRange } = rangeAddress;
      let range;

      if (customRange) {
        const namedRange = context.workbook.names.getItemOrNullObject(customRange);
        await context.sync();
        if (namedRange.isNullObject) { return; }

        // If a sheet with a named item was deleted, that item will still exist in office but with an empty range
        const rangeOrNull = namedRange.getRangeOrNullObject();

        namedRange.delete();
        await context.sync();

        if (rangeOrNull.isNullObject) { return; }
        range = rangeOrNull;

      }
      else if (address && worksheetId) {
        const worksheet = context.workbook.worksheets.getItemOrNullObject(worksheetId);
        await context.sync();

        if (worksheet.isNullObject) { return; }
        range = worksheet.getRange(address);
      }

      if (!range) { return; }
      range.clear();

      await context.sync();

    });
  }

  async createNamedRange(name, worksheetId, address) {
    await Excel.run(async (context) => {
      const namedRange = context.workbook.names.getItemOrNullObject(name);
      await context.sync();
      if (!namedRange.isNullObject) {
        if (worksheetId && address) {
          const worksheet = context.workbook.worksheets.getItem(worksheetId);
          worksheet.load('name');
          await context.sync();
          namedRange.formula = `='${worksheet.name}'!${address}`;
          await context.sync();
          console.log(`Named range '${name}' updated to ${worksheet.name}!${address}`);
        } else {
          console.log(`Named range '${name}' already exists, skipping creation`);
        }
        return;
      }
      const worksheet = context.workbook.worksheets.getItem(worksheetId);
      const range = worksheet.getRange(address);
      context.workbook.names.add(name, range);
      await context.sync();
    });
  }

  async deleteNamedRange(name) {
    await Excel.run(async (context) => {
      const namedItem = context.workbook.names.getItem(name);
      namedItem.delete();
      await context.sync();
    });
  }

  async selectRange(worksheedId, addresses) {
    return await Excel.run(async (context) => {
      const worksheet = context.workbook.worksheets.getItem(worksheedId);
      const rangeAreas = worksheet.getRanges(addresses.join(','));
      rangeAreas.select();
      await context.sync();
    })
  }

}

export default new OfficeModel();
